/**
 * Editor Top Bar Component
 * Contains page info, undo/redo, save, publish controls
 */
import React, { useState, useEffect, useCallback } from "react";
import { useEditor } from "@craftjs/core";
import {
  Box,
  Button as MuiButton,
  IconButton,
  Typography,
  Paper,
  Chip,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Undo as UndoIcon,
  Redo as RedoIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Publish as PublishIcon,
  Unpublished as UnpublishedIcon,
  ChevronLeft as ChevronLeftIcon,
  Check as CheckIcon,
  Devices as DevicesIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
  Schedule as ScheduleIcon,
  Code as CodeIcon,
} from "@mui/icons-material";
import { PageData } from "../../types";
import { ShortcutsHelp } from "./ShortcutsHelp";

interface EditorTopBarProps {
  page: PageData;
  onSave: (json: string) => Promise<void>;
  onUpdatePage: (updates: Partial<PageData>) => Promise<void>;
  isPreview: boolean;
  setIsPreview: (v: boolean) => void;
  onBack?: () => void;
  onShowPreviewModal?: () => void;
  // Domain-related props for Publish
  hasDomain?: boolean;
  isDomainVerified?: boolean;
  isSitePublished?: boolean;
  isCurrentSitePublished?: boolean; // Is THIS site the one that's published?
  publishedSiteName?: string | null; // Name of the site that's currently published (if different)
  onShowDomainModal?: () => void;
  onPublishSite?: () => void;
  onPublishReplace?: () => void; // Unpublish other site and publish this one
  onUnpublishSite?: () => void;
  isPublishing?: boolean;
  // Site URL and Last Published
  siteUrl?: string | null;
  lastPublished?: string | null;
  // Export React
  onExportReact?: () => void;
  isExporting?: boolean;
}

export const EditorTopBar: React.FC<EditorTopBarProps> = ({
  page,
  onSave,
  onUpdatePage,
  isPreview,
  setIsPreview,
  onBack,
  onShowPreviewModal,
  hasDomain,
  isDomainVerified,
  isSitePublished,
  isCurrentSitePublished,
  publishedSiteName,
  onShowDomainModal,
  onPublishSite,
  onPublishReplace,
  onUnpublishSite,
  isPublishing,
  siteUrl,
  lastPublished,
  onExportReact,
  isExporting,
}) => {
  const { query, actions, canUndo, canRedo } = useEditor((state, query) => ({
    canUndo: query.history.canUndo(),
    canRedo: query.history.canRedo(),
  }));
  const [saving, setSaving] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  // Copy URL to clipboard
  const handleCopyUrl = useCallback(async () => {
    if (!siteUrl) return;
    const fullUrl = `https://${siteUrl}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch {
      // Fallback
      const textArea = document.createElement("textarea");
      textArea.value = fullUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    }
  }, [siteUrl]);

  // Format relative time
  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleSave = useCallback(async () => {
    if (saving) return; // Prevent double save
    setSaving(true);
    try {
      await onSave(query.serialize());
      // Show success indicator briefly
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 2000);
    } finally {
      setSaving(false);
    }
  }, [onSave, query, saving]);

  // Listen for keyboard shortcut save event
  useEffect(() => {
    const handleEditorSave = () => handleSave();
    window.addEventListener("editor-save", handleEditorSave);
    return () => window.removeEventListener("editor-save", handleEditorSave);
  }, [handleSave]);

  return (
    <Paper
      square
      elevation={0}
      sx={{
        height: 48,
        bgcolor: "white",
        borderBottom: 1,
        borderColor: "divider",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
      }}
    >
      {/* Left - Page info + Undo/Redo */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {onBack && (
          <Tooltip title="Back to pages" arrow>
            <IconButton onClick={onBack} size="small" sx={{ color: "text.secondary" }}>
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
        )}
        <Typography variant="subtitle1" fontWeight={600} color="text.primary">
          {page.name}
        </Typography>
        <Chip
          label={page.isPublished ? "Active" : "Inactive"}
          size="small"
          color={page.isPublished ? "success" : "default"}
          sx={{ height: 22, fontSize: 12 }}
        />

        {/* Undo/Redo buttons - Enhanced */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            ml: 1,
            pl: 1.5,
            borderLeft: 1,
            borderColor: "divider",
          }}
        >
          <Tooltip title="Undo (Ctrl+Z)" arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => actions.history.undo()}
                disabled={!canUndo}
                sx={{ color: "text.secondary" }}
              >
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Redo (Ctrl+Shift+Z)" arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => actions.history.redo()}
                disabled={!canRedo}
                sx={{ color: "text.secondary" }}
              >
                <RedoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <ShortcutsHelp />
          {/* Export as React TSX */}
          {onExportReact && (
            <Tooltip title={isExporting ? "Exporting..." : "Export as React components (TSX)"} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={onExportReact}
                  disabled={isExporting}
                  sx={{ color: isExporting ? "primary.main" : "text.secondary", ml: 0.5 }}
                >
                  {isExporting ? <CircularProgress size={18} /> : <CodeIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>

        {/* Site URL + Last Published - only show if THIS site is published */}
        {siteUrl && isCurrentSitePublished && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              ml: 1,
              pl: 1.5,
              borderLeft: 1,
              borderColor: "divider",
            }}
          >
            <Tooltip title={copiedUrl ? "Copied!" : `https://${siteUrl}`} arrow>
              <Chip
                label={siteUrl.length > 30 ? `${siteUrl.substring(0, 30)}...` : siteUrl}
                size="small"
                onClick={handleCopyUrl}
                onDelete={() => window.open(`https://${siteUrl}`, "_blank")}
                deleteIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                icon={copiedUrl ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                sx={{
                  height: 24,
                  fontSize: 11,
                  fontFamily: "monospace",
                  bgcolor: copiedUrl ? "success.50" : "primary.50",
                  color: copiedUrl ? "success.main" : "primary.main",
                  "& .MuiChip-icon": { color: "inherit" },
                  "& .MuiChip-deleteIcon": { color: "inherit" },
                  cursor: "pointer",
                }}
              />
            </Tooltip>
            {lastPublished && (
              <Tooltip title={`Last published: ${new Date(lastPublished).toLocaleString()}`} arrow>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
                  <ScheduleIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption" sx={{ fontSize: 11 }}>
                    {formatRelativeTime(lastPublished)}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      {/* Center - Edit mode indicator + Preview button */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, bgcolor: "grey.100", borderRadius: 1, p: 0.5 }}>
        <Box
          sx={{
            px: 2,
            py: 0.5,
            bgcolor: "white",
            borderRadius: 0.5,
            boxShadow: 1,
            fontSize: 13,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <EditIcon sx={{ fontSize: 16 }} />
          Edit
        </Box>
        {onShowPreviewModal && (
          <Tooltip title="Preview in device frames (Desktop, Tablet, Mobile)" arrow>
            <MuiButton
              size="small"
              onClick={onShowPreviewModal}
              startIcon={<DevicesIcon sx={{ fontSize: 16 }} />}
              sx={{
                px: 2,
                py: 0.5,
                textTransform: "none",
                fontSize: 13,
                color: "text.secondary",
                "&:hover": { bgcolor: "grey.200" },
              }}
            >
              Preview
            </MuiButton>
          </Tooltip>
        )}
      </Box>

      {/* Right - Actions */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {/* Page Active/Inactive Toggle */}
        <Tooltip title={page.isPublished ? "Set page as inactive" : "Set page as active"} arrow>
          <MuiButton
            size="small"
            startIcon={
              page.isPublished ? <CheckIcon sx={{ fontSize: 16 }} /> : <UnpublishedIcon sx={{ fontSize: 16 }} />
            }
            onClick={() => onUpdatePage({ isPublished: !page.isPublished })}
            sx={{
              textTransform: "none",
              color: page.isPublished ? "success.main" : "text.secondary",
            }}
          >
            {page.isPublished ? "Active" : "Inactive"}
          </MuiButton>
        </Tooltip>

        {/* Publish/Unpublish Button */}
        {(onShowDomainModal || onPublishSite || onUnpublishSite) && (
          <>
            {isCurrentSitePublished ? (
              // This site is published - show Unpublish button
              <Tooltip title="Take your site offline" arrow>
                <MuiButton
                  size="small"
                  variant="outlined"
                  disabled={isPublishing}
                  startIcon={isPublishing ? <CircularProgress size={16} color="inherit" /> : <UnpublishedIcon sx={{ fontSize: 16 }} />}
                  onClick={() => onUnpublishSite?.()}
                  sx={{
                    textTransform: "none",
                    borderColor: "warning.main",
                    color: "warning.main",
                    "&:hover": {
                      borderColor: "warning.dark",
                      bgcolor: "warning.50",
                    },
                  }}
                >
                  {isPublishing ? "Unpublishing..." : "Unpublish"}
                </MuiButton>
              </Tooltip>
            ) : isSitePublished && publishedSiteName ? (
              // Another site is published - show warning and "Publish แทน" button
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  size="small"
                  label={`"${publishedSiteName}" published อยู่`}
                  sx={{
                    height: 24,
                    fontSize: 11,
                    bgcolor: "warning.50",
                    color: "warning.dark",
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
                <Tooltip title={`Unpublish "${publishedSiteName}" และ publish site นี้แทน`} arrow>
                  <MuiButton
                    size="small"
                    variant="contained"
                    disabled={isPublishing}
                    startIcon={
                      isPublishing ? <CircularProgress size={16} color="inherit" /> : <PublishIcon sx={{ fontSize: 16 }} />
                    }
                    onClick={() => {
                      if (isPublishing) return;
                      onPublishReplace?.();
                    }}
                    sx={{
                      textTransform: "none",
                      background: isPublishing
                        ? "linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)"
                        : "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
                      "&:hover": {
                        background: isPublishing
                          ? "linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)"
                          : "linear-gradient(135deg, #D97706 0%, #B45309 100%)",
                      },
                      "&.Mui-disabled": {
                        color: "white",
                      },
                    }}
                  >
                    {isPublishing ? "Publishing..." : "Publish แทน"}
                  </MuiButton>
                </Tooltip>
              </Box>
            ) : (
              // No site is published - show Publish button
              <Tooltip title={isPublishing ? "Publishing..." : "Publish your site"} arrow>
                <MuiButton
                  size="small"
                  variant="contained"
                  disabled={isPublishing}
                  startIcon={
                    isPublishing ? <CircularProgress size={16} color="inherit" /> : <PublishIcon sx={{ fontSize: 16 }} />
                  }
                  onClick={() => {
                    if (isPublishing) return;
                    onPublishSite?.();
                  }}
                  sx={{
                    textTransform: "none",
                    background: isPublishing
                      ? "linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)"
                      : "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
                    "&:hover": {
                      background: isPublishing
                        ? "linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)"
                        : "linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)",
                    },
                    "&.Mui-disabled": {
                      color: "white",
                    },
                  }}
                >
                  {isPublishing ? "Publishing..." : "Publish"}
                </MuiButton>
              </Tooltip>
            )}
          </>
        )}

        <Tooltip title="Save changes (Ctrl+S)" arrow>
          <MuiButton
            variant="contained"
            size="small"
            startIcon={
              saving ? (
                <Box
                  component="span"
                  sx={{
                    width: 16,
                    height: 16,
                    border: "2px solid",
                    borderColor: "rgba(255,255,255,0.3)",
                    borderTopColor: "white",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
              ) : showSaveSuccess ? (
                <CheckIcon sx={{ fontSize: 16 }} />
              ) : (
                <SaveIcon sx={{ fontSize: 16 }} />
              )
            }
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: "none",
              bgcolor: showSaveSuccess ? "success.main" : "primary.main",
              "&:hover": {
                bgcolor: showSaveSuccess ? "success.dark" : "primary.dark",
              },
            }}
          >
            {saving ? "Saving..." : showSaveSuccess ? "Saved!" : "Save"}
          </MuiButton>
        </Tooltip>
      </Box>
    </Paper>
  );
};

export default EditorTopBar;
